import { createContext, FC, useCallback, useContext, useMemo, useState } from 'react';

import { A11yHiddenBox } from '@rover/kibble/styles';

export type A11yContextValue = {
  screenReaderAnnouncement: string;
  setScreenReaderAnnouncement: (announcement: string) => void;
};

export const A11yContext = createContext<A11yContextValue>({
  screenReaderAnnouncement: '',
  setScreenReaderAnnouncement: () => {},
});

export const A11yContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState<string>('');
  const handleSetScreenReaderAnnouncement = useCallback(
    (announcement: string) => {
      setTimeout(() => {
        setScreenReaderAnnouncement(announcement);
      }, 0);
    },
    [setScreenReaderAnnouncement]
  );
  const contextValueMemo = useMemo(
    () => ({
      screenReaderAnnouncement,
      setScreenReaderAnnouncement: handleSetScreenReaderAnnouncement,
    }),
    [screenReaderAnnouncement, handleSetScreenReaderAnnouncement]
  );

  return (
    <A11yContext.Provider value={contextValueMemo}>
      <A11yHiddenBox aria-live="polite">{screenReaderAnnouncement}</A11yHiddenBox>
      {children}
    </A11yContext.Provider>
  );
};

export const useA11yContext = (): A11yContextValue => useContext(A11yContext);
